import { XIcon } from 'lucide-react';

import { useBreakpointDown } from '@nesso/ui/hooks/use-breakpoint-down';
import { cn } from '@nesso/ui/lib/utils';
import { Button } from '@nesso/ui/primitives/button';
import { Dialog, DialogClose, DialogContent, DialogTitle } from '@nesso/ui/primitives/dialog';
import { Item, ItemContent, ItemTitle } from '@nesso/ui/primitives/item';
import { NessoSpinner } from '@nesso/ui/primitives/nesso-spinner';

import { type SheetProps, useSheetStore } from '@/shared/stores/sheet-store';

import { useScheduleSessionForm } from '../model/use-schedule-session-form';
import { ScheduleSessionForm } from './schedule-session-dialog/schedule-session-form';

type ScheduleSessionSheet = Extract<SheetProps, { type: 'sessions/schedule' }>;

// Beside `AddClient`, but exists only while `sessions/schedule` is in the stack. The form's
// state lives in `ScheduleSessionDialogContent`, keyed by the entry so a new client always resets.
export function ScheduleSessionDialog() {
    const openSheets = useSheetStore((state) => state.openSheets);
    const currentSheet = useSheetStore((state) => state.currentSheet);

    const entry = openSheets.find(
        (sheet): sheet is ScheduleSessionSheet => sheet.type === 'sessions/schedule',
    );

    if (!entry) return null;

    return (
        <ScheduleSessionDialogContent
            key={entry.data.client?.id ?? 'no-client'}
            entry={entry}
            isOpen={currentSheet === entry}
        />
    );
}

type ContentProps = {
    entry: ScheduleSessionSheet;
    isOpen: boolean;
};

// Stays mounted while `entry` is anywhere in the stack — `isOpen` false only hides the dialog via
// the `open` prop, so values survive an excursion like Add New Client on top of it.
function ScheduleSessionDialogContent({ entry, isOpen }: ContentProps) {
    const isXs = useBreakpointDown('xs');
    const closeCurrentSheet = useSheetStore((state) => state.closeCurrentSheet);
    const scheduleSession = useScheduleSessionForm({ client: entry.data.client });

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(open) => !open && !scheduleSession.isPending && closeCurrentSheet()}
        >
            <DialogContent
                fullscreen={isXs}
                showCloseButton={false}
                className={cn('gap-0 p-0', !isXs && 'max-h-[calc(100dvh-2rem)] overflow-y-auto')}
            >
                <ScheduleSessionDialogActions />
                {scheduleSession.isError ? (
                    <div className="flex flex-1 items-center justify-center p-12">
                        {/* Radix requires a `DialogTitle` for every open `DialogContent`; the actions
                        row's close control is the way out. */}
                        <DialogTitle className="sr-only">New Session</DialogTitle>
                        <Item variant="muted" size="sm" alignment="center">
                            <ItemContent>
                                <ItemTitle>Couldn't load New Session</ItemTitle>
                            </ItemContent>
                        </Item>
                    </div>
                ) : scheduleSession.isLoading ? (
                    <div className="flex flex-1 items-center justify-center p-12">
                        {/* Radix requires a `DialogTitle` for every open `DialogContent` — sr-only
                        since loading has no visible title, and distinct from "New Session" so
                        nothing waiting on that name is satisfied early. */}
                        <DialogTitle className="sr-only">Loading New Session</DialogTitle>
                        <NessoSpinner />
                    </div>
                ) : (
                    <ScheduleSessionForm
                        control={scheduleSession.form.control}
                        isPending={scheduleSession.isPending}
                        isXs={isXs}
                        onServiceChange={scheduleSession.onServiceChange}
                        onStartTimeChange={scheduleSession.onStartTimeChange}
                        onSubmit={scheduleSession.onSubmit}
                        onCancel={closeCurrentSheet}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
}

// Calendar dialogs keep a row above the title for actions, so the close moves out of the title row.
function ScheduleSessionDialogActions() {
    return (
        <div className="flex justify-end px-4 py-3">
            <DialogClose asChild>
                <Button type="button" variant="ghost" size="icon-sm">
                    <XIcon />
                    <span className="sr-only">Close</span>
                </Button>
            </DialogClose>
        </div>
    );
}
